/**
 * v68 — first-login "Connect your books" wizard.
 *
 * Shows once to owners/managers who haven't dismissed it and don't yet have an
 * accounting connection. Three steps:
 *   1. Pick a provider (Xero / QBO / Skip)
 *   2. Live sync animation showing what's being pushed
 *   3. Snapshot: top 5 jobs by margin (from /reports/job-profitability)
 *
 * Trigger conditions, evaluated in <Dashboard>:
 *   - Logged-in user role is owner or manager
 *   - User.onboarding_accounting_dismissed_at is null/undefined
 *   - /api/accounting/connections returns []
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import {
  X, Plug, Loader2, CheckCircle2, ArrowRight, BarChart3, TrendingUp, Sparkles,
} from "lucide-react";
import { API_BASE, formatApiError } from "./AuthContext";

const PROVIDERS = [
  { id: "xero", name: "Xero",
    blurb: "Push contacts + invoices, pull payments back automatically.",
    accent: "var(--accent)" },
  { id: "qbo", name: "QuickBooks Online",
    blurb: "Same flow — clients, invoices, payment sync.",
    accent: "var(--moss)" },
];

export default function OnboardingWizard({ onDismiss }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);           // 1 → pick · 2 → sync · 3 → snapshot
  const [provider, setProvider] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [topJobs, setTopJobs] = useState([]);

  const dismiss = async () => {
    try { await axios.post(`${API_BASE}/auth/onboarding/dismiss`); } catch { /* non-fatal */ }
    onDismiss();
  };

  const skip = async () => {
    await dismiss();
    toast("Wizard skipped — you can connect any time from Settings → Accounting sync.", { duration: 4000 });
  };

  const connectAndSync = async (providerId) => {
    setProvider(providerId);
    setStep(2);
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/accounting/connect`, { provider: providerId });
      const r = await axios.post(`${API_BASE}/accounting/sync`, { provider: providerId });
      const s = r.data?.[providerId]?.summary || {};
      setSummary(s);
      // Pull job profitability snapshot
      const prof = await axios.get(`${API_BASE}/reports/job-profitability?days=90`);
      setTopJobs((prof.data?.jobs || []).slice(0, 5));
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't complete the connect-and-sync flow");
      setStep(1);
    } finally { setBusy(false); }
  };

  const finishToReports = async () => {
    await dismiss();
    navigate("/reports");
  };
  const finishToAccounting = async () => {
    await dismiss();
    navigate("/settings/accounting");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55"
         data-testid="onboarding-wizard">
      <div className="w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden"
           style={{ background: "var(--paper)" }}>
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="inline-flex items-center gap-2">
            <span className="overline">First-time setup · Accounting sync</span>
          </div>
          <button onClick={skip} className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                  data-testid="onboarding-close-x" title="Skip for now">
            <X size={18}/>
          </button>
        </div>

        {/* progress strip */}
        <div className="flex border-b" style={{ borderColor: "var(--border)" }}>
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1 h-1"
                 style={{ background: n <= step ? "var(--accent)" : "var(--subtle)" }}/>
          ))}
        </div>

        {/* body */}
        <div className="px-6 py-8">
          {step === 1 && (
            <StepPick onPick={connectAndSync}/>
          )}
          {step === 2 && (
            <StepSync busy={busy} provider={provider}/>
          )}
          {step === 3 && (
            <StepSnapshot summary={summary} provider={provider} topJobs={topJobs}/>
          )}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-[var(--subtle)]"
             style={{ borderColor: "var(--border)" }}>
          {step === 1 && (
            <>
              <span className="text-[11px] text-[var(--ink-3)]">
                You can run this any time from <Link to="/settings/accounting" className="underline">Settings → Accounting sync</Link>.
              </span>
              <button onClick={skip} className="text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                      data-testid="onboarding-skip-btn">
                Skip for now
              </button>
            </>
          )}
          {step === 2 && (
            <span className="text-[11px] text-[var(--ink-3)]">
              Hold tight — this typically completes in a few seconds.
            </span>
          )}
          {step === 3 && (
            <>
              <button onClick={finishToAccounting}
                      className="text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                      data-testid="onboarding-go-accounting-btn">
                Manage sync settings
              </button>
              <button onClick={finishToReports}
                      className="btn-primary text-[12px] inline-flex items-center gap-1"
                      data-testid="onboarding-finish-btn">
                See full profitability report <ArrowRight size={12}/>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StepPick({ onPick }) {
  return (
    <>
      <h2 className="font-heading text-3xl mb-2">
        Sync your <em className="italic" style={{ color: "var(--accent)" }}>books</em> in one click.
      </h2>
      <p className="text-[13px] text-[var(--ink-3)] mb-6 max-w-lg">
        Connect Xero or QuickBooks and we'll push your clients & sent invoices and pull payment
        status back — then show you a profitability snapshot right after.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {PROVIDERS.map((p) => (
          <button key={p.id}
                  onClick={() => onPick(p.id)}
                  data-testid={`onboarding-pick-${p.id}`}
                  className="text-left card p-4 hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  style={{ borderTop: `3px solid ${p.accent}` }}>
            <div className="inline-flex items-center gap-2 mb-1">
              <Plug size={14} style={{ color: p.accent }}/>
              <span className="font-heading text-xl">{p.name}</span>
            </div>
            <div className="text-[11.5px] text-[var(--ink-3)]">{p.blurb}</div>
            <div className="mt-3 text-[12px] inline-flex items-center gap-1"
                 style={{ color: p.accent }}>
              Connect (stub) <ArrowRight size={11}/>
            </div>
          </button>
        ))}
      </div>
      <div className="text-[11px] text-[var(--ink-3)] italic mt-4 inline-flex items-center gap-1">
        <Sparkles size={10}/> First slice uses a stub provider for demos. Real OAuth tenants are a one-line swap when your creds are ready.
      </div>
    </>
  );
}

function StepSync({ busy, provider }) {
  return (
    <div className="text-center py-6">
      <Loader2 size={36} className="animate-spin mx-auto text-[var(--accent)] mb-4"/>
      <div className="font-heading text-2xl mb-2">
        Syncing with <em className="italic">{provider === "xero" ? "Xero" : "QuickBooks Online"}</em>…
      </div>
      <div className="text-[13px] text-[var(--ink-3)]">
        Pushing contacts and invoices, then pulling payment status.
      </div>
      <div className="text-[11px] text-[var(--ink-3)] mt-4 italic">
        {busy ? "Working…" : "Almost done."}
      </div>
    </div>
  );
}

function StepSnapshot({ summary = {}, provider, topJobs = [] }) {
  const totalBilled = topJobs.reduce((s, j) => s + (j.billed || 0), 0);
  const totalMargin = topJobs.reduce((s, j) => s + (j.margin || 0), 0);
  const fmt = (n) => `$${Math.round(n || 0).toLocaleString()}`;
  const provLabel = provider === "xero" ? "Xero" : "QuickBooks Online";

  return (
    <>
      <div className="inline-flex items-center gap-2 mb-2 text-[var(--moss)]">
        <CheckCircle2 size={18}/>
        <span className="font-heading text-2xl">
          {provLabel} is <em className="italic">connected</em>.
        </span>
      </div>
      <p className="text-[12.5px] text-[var(--ink-3)] mb-5">
        Sync completed — {summary.contacts_pushed || 0} contacts pushed, {summary.invoices_pushed || 0} invoices pushed,
        {" "}{summary.payments_pulled || 0} payments pulled. Future changes will sync every 15 minutes automatically.
      </p>

      <div className="grid grid-cols-3 gap-2 mb-5">
        <Stat label="Contacts" value={summary.contacts_pushed || 0} accent="var(--accent)"/>
        <Stat label="Invoices" value={summary.invoices_pushed || 0} accent="var(--moss)"/>
        <Stat label="Payments" value={summary.payments_pulled || 0} accent="var(--ink-2)"/>
      </div>

      <div className="card p-4">
        <div className="overline mb-1 inline-flex items-center gap-1.5">
          <BarChart3 size={11}/> Top 5 jobs by margin (last 90d)
        </div>
        {topJobs.length === 0 ? (
          <div className="text-[12px] text-[var(--ink-3)] italic mt-2"
               data-testid="onboarding-snapshot-empty">
            No closed jobs yet — once you log time and send invoices, this snapshot fills out.
          </div>
        ) : (
          <>
            <div className="space-y-1.5 mt-2" data-testid="onboarding-top-jobs">
              {topJobs.map((j) => (
                <div key={j.job_id} className="flex items-center justify-between gap-3 text-[12px]">
                  <span className="truncate flex-1 text-[var(--ink-2)]" title={j.title}>{j.title}</span>
                  <span className="text-[var(--ink-3)] font-mono">{fmt(j.billed)}</span>
                  <span className="font-mono font-medium"
                        style={{ color: (j.margin || 0) >= 0 ? "var(--moss)" : "#B91C1C", minWidth: 64, textAlign: "right" }}>
                    {(j.margin || 0) < 0 ? "-" : ""}{fmt(Math.abs(j.margin || 0))}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-[var(--border)] grid grid-cols-2 gap-3 text-[11px]">
              <div className="inline-flex items-center gap-1.5 text-[var(--ink-3)]">
                <span>Billed</span>
                <span className="text-[var(--ink-2)] font-mono">{fmt(totalBilled)}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 text-[var(--ink-3)]">
                <TrendingUp size={10}/>
                <span>Margin</span>
                <span className="font-mono"
                      style={{ color: totalMargin >= 0 ? "var(--moss)" : "#B91C1C" }}>
                  {totalMargin < 0 ? "-" : ""}{fmt(Math.abs(totalMargin))}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="card p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-0.5">{label}</div>
      <div className="font-heading text-2xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}
